import React, { useEffect, useRef } from "react";
import { StepBoundary, TimelineStep } from "../../../types/timeline";
import { pixelToFrame } from "../../../utils/timelineMath";
import { Playhead } from "./Playhead";
import { StepRangeBar } from "./StepRangeBar";

interface TimelineTrackProps {
  totalFrames: number;
  currentFrame: number;
  steps: TimelineStep[];
  selectedStepId: string | null;
  activeStepId: string | null;
  stepRangeLabel: string;
  startHandleLabel: string;
  endHandleLabel: string;
  handleForLabel: string;
  playheadLabel: string;
  seekLabel: string;
  disabled?: boolean;
  onSeek: (frame: number) => void;
  onCreateStep: (frame: number) => void;
  onSelectStep: (stepId: string) => void;
  onDragBoundary: (
    stepId: string,
    boundary: StepBoundary,
    frame: number,
  ) => void;
  onDragEnd: (stepId: string) => void;
  onBoundaryFocus: (boundary: StepBoundary) => void;
}

interface DragState {
  stepId: string;
  boundary: StepBoundary;
}

type InteractionState =
  | { mode: "boundary"; drag: DragState }
  | { mode: "playhead" };

export const TimelineTrack = React.memo(function TimelineTrack({
  totalFrames,
  currentFrame,
  steps,
  selectedStepId,
  activeStepId,
  stepRangeLabel,
  startHandleLabel,
  endHandleLabel,
  handleForLabel,
  playheadLabel,
  seekLabel,
  disabled = false,
  onSeek,
  onCreateStep,
  onSelectStep,
  onDragBoundary,
  onDragEnd,
  onBoundaryFocus,
}: TimelineTrackProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const interactionRef = useRef<InteractionState | null>(null);
  const latestClientXRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  const processDragFrame = () => {
    rafRef.current = null;
    if (
      !trackRef.current ||
      !interactionRef.current ||
      latestClientXRef.current === null
    )
      return;

    const rect = trackRef.current.getBoundingClientRect();
    const pixel = latestClientXRef.current - rect.left;
    const nextFrame = pixelToFrame(pixel, rect.width, totalFrames);

    if (interactionRef.current.mode === "playhead") {
      onSeek(nextFrame);
      return;
    }

    onDragBoundary(
      interactionRef.current.drag.stepId,
      interactionRef.current.drag.boundary,
      nextFrame,
    );
  };

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent) => {
      if (!interactionRef.current || disabled) return;
      latestClientXRef.current = e.clientX;
      if (rafRef.current === null) {
        rafRef.current = window.requestAnimationFrame(processDragFrame);
      }
    };

    const handlePointerUp = () => {
      if (!interactionRef.current) return;
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      if (trackRef.current && latestClientXRef.current !== null) {
        const rect = trackRef.current.getBoundingClientRect();
        const pixel = latestClientXRef.current - rect.left;
        const nextFrame = pixelToFrame(pixel, rect.width, totalFrames);

        if (interactionRef.current.mode === "playhead") {
          onSeek(nextFrame);
        } else {
          onDragBoundary(
            interactionRef.current.drag.stepId,
            interactionRef.current.drag.boundary,
            nextFrame,
          );
          onDragEnd(interactionRef.current.drag.stepId);
        }
      }
      interactionRef.current = null;
      latestClientXRef.current = null;
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, [disabled, onDragBoundary, onDragEnd, totalFrames]);

  return (
    <div
      ref={trackRef}
      className="timeline-track"
      onClick={(e) => {
        if (disabled || !trackRef.current) return;

        const target = e.target as HTMLElement;
        const clickedOnMarker = target.closest(".timeline-step-range");
        const clickedOnPlayhead = target.closest(".timeline-playhead-handle");
        const rect = trackRef.current.getBoundingClientRect();
        const pixel = e.clientX - rect.left;
        const frame = pixelToFrame(pixel, rect.width, totalFrames);
        onSeek(frame);

        if (!clickedOnMarker && !clickedOnPlayhead) {
          onCreateStep(frame);
        }
      }}
      style={{
        position: "relative",
        height: 44,
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.1)",
        background:
          "linear-gradient(to bottom, rgba(23, 29, 43, 0.88), rgba(10, 14, 22, 0.92))",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
        overflow: "visible",
        cursor: disabled ? "not-allowed" : "crosshair",
      }}
      data-oid="vdz380f"
    >
      {steps.map((step) => (
        <StepRangeBar
          key={step.id}
          step={step}
          totalFrames={totalFrames}
          isSelected={step.id === selectedStepId}
          isActive={step.id === activeStepId}
          stepRangeLabel={stepRangeLabel}
          startHandleLabel={startHandleLabel}
          endHandleLabel={endHandleLabel}
          handleForLabel={handleForLabel}
          disabled={disabled}
          onSelect={onSelectStep}
          onHandleFocus={onBoundaryFocus}
          onHandlePointerDown={(stepId, boundary, pointerEvent) => {
            if (disabled) return;
            pointerEvent.stopPropagation();
            pointerEvent.currentTarget.setPointerCapture(
              pointerEvent.pointerId,
            );
            interactionRef.current = {
              mode: "boundary",
              drag: { stepId, boundary },
            };
            latestClientXRef.current = pointerEvent.clientX;
            onSelectStep(stepId);
            onBoundaryFocus(boundary);
            if (rafRef.current === null) {
              rafRef.current = window.requestAnimationFrame(processDragFrame);
            }
          }}
          data-oid="2o0i7zd"
        />
      ))}

      <Playhead
        currentFrame={currentFrame}
        totalFrames={totalFrames}
        playheadLabel={playheadLabel}
        seekLabel={seekLabel}
        disabled={disabled}
        onPointerDown={(pointerEvent) => {
          if (disabled) return;
          pointerEvent.stopPropagation();
          pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
          interactionRef.current = { mode: "playhead" };
          latestClientXRef.current = pointerEvent.clientX;
          if (rafRef.current === null) {
            rafRef.current = window.requestAnimationFrame(processDragFrame);
          }
        }}
        data-oid="35k93hu"
      />
    </div>
  );
});
